import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Card, CardContent, CardHeader, Avatar, Typography, TextField, Button, Grid, MenuItem } from '@mui/material';
import axios from 'axios';
import mqtt from 'mqtt';

export default function UpdateStreetlamp() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const mqttClient = useRef(null);

  const lamp = state || {};

  const {
    id,
    devEUNumber,
    company,
    installationAddress,
    status,
    currentPower,
    currentDimming,
    lightingmodeid
  } = lamp;

  const [addr, setAddr] = useState(installationAddress ?? '');
  const [stat, setStat] = useState(status ?? '');
  const [lightingModeId, setLightingModeId] = useState(lightingmodeid ?? '');
  const [lightingModes, setLightingModes] = useState([]);
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState(lamp.zonaid ?? '');

  const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

  // MQTT
  useEffect(() => {
    mqttClient.current = mqtt.connect('wss://smartlight.bhtelecom.ba:8083');

    mqttClient.current.on('connect', () => {
      console.log('Povezan na MQTT broker');
    });

    // ✅ ERROR HANDLER
    mqttClient.current.on('error', (err) => {
      console.error('MQTT error', err);
    });

    return () => mqttClient.current?.end();
  }, []);

  // ZONE
  useEffect(() => {
    axios
      .get(`${SERVER_URL}/api/zonas`)
      .then(res => setZones(res.data))
      .catch(err => console.error('Ne mogu dohvatiti zone', err));
  }, []);

  // LIGHTING MODES
  useEffect(() => {
    axios
      .get(`${SERVER_URL}/api/lightingmodes`)
      .then((res) => setLightingModes(res.data))
      .catch(err => console.error('Ne mogu dohvatiti lighting modes', err));
  }, []);

  const notifyLightingModeChanged = (devEUNumber) => {
    if (!mqttClient.current) return;

    const payload = {
      devEUNumber,
      type: 'LightingModeChangedforLamp',
      timestamp: Date.now()
    };

    mqttClient.current.publish(
      '/smartmap/LightingModeChanged',
      JSON.stringify(payload),
      { qos: 1 }
    );
  };

  const updateAPIData = async () => {
    try {
      await axios.put(
        `${SERVER_URL}/api/streetlamps/${id}`,
        {
          installationAddress: addr,
          lightingmodeid: lightingModeId,
          zonaid: zoneId
        }
      );

      notifyLightingModeChanged(devEUNumber);
      navigate('/dashboard/LampsListAll', { replace: true });

    } catch (err) {
      console.error('UPDATE ERROR:', err.response ?? err);
      alert('Update lampe nije uspio.');
    }
  };

  // 🔥 DARK SELECT CONFIG
  const darkSelect = {
    style: { color: '#ff9800' },
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: '#0f172a',
          color: '#ff9800',
          '& .MuiMenuItem-root': {
            '&:hover': {
              bgcolor: '#1e293b'
            },
            '&.Mui-selected': {
              bgcolor: '#1e40af'
            }
          }
        }
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#001536', minHeight: '100vh', padding: 20 }}>
      <Grid container justifyContent="center">
        <Card sx={{ maxWidth: 700, width: '100%', backgroundColor: '#00122E', color: '#ff9800' }}>

          <CardHeader
            avatar={<Avatar sx={{ width: 60, height: 60 }}>{(stat || 'L')[0]}</Avatar>}
            title={<Typography variant="h6">{`Lamp: ${devEUNumber}`}</Typography>}
            subheader={<Typography variant="body2">{company}</Typography>}
          />

          <CardContent>

            <TextField
              label="Adresa instalacije"
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ style: { color: '#ff9800' } }}
              InputProps={{ style: { color: '#ff9800' } }}
            />

            {/* LIGHTING MODE */}
            <TextField
              select
              label="Lighting režim"
              value={lightingModeId}
              onChange={(e) => setLightingModeId(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ style: { color: '#ff9800' } }}
              SelectProps={darkSelect}
            >
              <MenuItem value={0}>
                <em>Bez vlastitog režima, režim preuzima iz zone</em>
              </MenuItem>

              {lightingModes.map((mode) => (
                <MenuItem key={mode.id} value={mode.id}>
                  {mode.name}
                </MenuItem>
              ))}
            </TextField>

            {/* ZONA */}
            <TextField
              select
              label="Zona"
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ style: { color: '#ff9800' } }}
              SelectProps={darkSelect}
            >
              <MenuItem value={0}>
                <em>Bez zone</em>
              </MenuItem>

              {zones.map((z) => (
                <MenuItem key={z.id} value={z.id}>
                  {z.name}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                sx={{
                  minWidth: 100,
                  backgroundColor: '#001B44',
                  color: '#ff9800',                // 🔥 tekst orange
                  border: '1px solid #ff9800',     // 🔥 border orange
                  '&:hover': {
                    backgroundColor: '#001B44',
                    border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                    color: '#ffa726'
                  }
                }}
                onClick={() => navigate(-1)}
              >
                Odustani
              </Button>

              <Button
                sx={{
                  minWidth: 100,
                  backgroundColor: '#001B44',
                  color: '#ff9800',                // 🔥 tekst orange
                  border: '1px solid #ff9800',     // 🔥 border orange
                  '&:hover': {
                    backgroundColor: '#001B44',
                    border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                    color: '#ffa726'
                  }
                }}
                onClick={updateAPIData}
              >
                Spasi
              </Button>
            </Box>

          </CardContent>
        </Card>
      </Grid>
    </div>
  );
}